const GUEST_DRAFT_CART_KEY = 'guestDraftCartId'

/** @param {string | undefined} memberId */
export function getCartStorageKey(memberId) {
	return memberId ? `cart:${memberId}` : 'cart'
}

export function getGuestDraftCartId() {
	try {
		return window.localStorage.getItem(GUEST_DRAFT_CART_KEY)
	} catch {
		return null
	}
}

/** @param {string} cartId */
export function setGuestDraftCartId(cartId) {
	window.localStorage.setItem(GUEST_DRAFT_CART_KEY, cartId)
}

export function clearGuestDraftCartId() {
	window.localStorage.removeItem(GUEST_DRAFT_CART_KEY)
}

/** @param {object} shipping */
export function shippingToGuestForm(shipping) {
	if (!shipping?.address) return null
	const name = [shipping.firstName, shipping.lastName].filter(Boolean).join(' ')
	return {
		name: name || null,
		phone: shipping.phoneNumber || null,
		address: shipping.address.address || null,
		unit: shipping.address.unit || null,
		city: shipping.address.city || null,
		state: shipping.address.state || null,
		zip: shipping.address.zip || null,
		country: shipping.address.country || 'United States',
	}
}

/** @param {object} shipping */
export function shippingToSelectedAddress(shipping) {
	const form = shippingToGuestForm(shipping)
	if (!form) return null
	return {
		firstName: shipping.firstName || form.name?.split(' ')?.[0] || '',
		lastName: shipping.lastName || form.name?.split(' ')?.slice(1)?.join(' ') || '',
		address: form.address,
		unit: form.unit || '',
		city: form.city,
		state: form.state,
		zip: form.zip,
		country: form.country,
		phone: form.phone,
	}
}

/** @param {object} address @param {(phone: string) => string} stripPhone */
export function guestFormToShipping(address, stripPhone) {
	if (!address) return null
	const parts = (address.name || '').trim().split(/\s+/)
	return {
		firstName: parts[0] || '',
		lastName: parts.slice(1).join(' ') || '',
		phoneNumber: stripPhone(address.phone),
		address: {
			address: address.address,
			unit: address.unit || '',
			city: address.city,
			state: address.state,
			zip: address.zip,
			country: address.country,
		},
	}
}

/** @param {string} key */
export function parseLocalCart(key) {
	try {
		const raw = window.localStorage.getItem(key)
		if (!raw) return null
		const parsed = JSON.parse(raw)
		return Array.isArray(parsed) ? parsed : null
	} catch {
		return null
	}
}

/** @param {unknown} itemRef @param {string} productId */
export function saleItemMatchesLimitation(itemRef, productId) {
	if (!productId) return false
	if (typeof itemRef === 'string') return itemRef === productId
	return itemRef?.id == productId
}

/** @param {object} sale @param {string} productId */
export function saleAppliesToItem(sale, productId) {
	const items = sale?.limitations?.items
	if (!items?.length) return true
	return items.some((i) => saleItemMatchesLimitation(i, productId))
}

/** @param {object} ci */
export function normalizeCartLine(ci) {
	if (!ci) return null
	const item = ci.item
	const id = typeof item === 'string' ? item : item?.id
	if (!id) return null

	const normalizedItem =
		typeof item === 'object' && item !== null && !Array.isArray(item) ? { ...item, id } : { id }

	if (!Number.isFinite(Number(normalizedItem.price))) return null

	return {
		quantity: ci.quantity || 1,
		...(ci.variant ? { variant: ci.variant } : {}),
		item: normalizedItem,
	}
}

/** @param {unknown[]} items */
export function normalizeCartItems(items) {
	if (!Array.isArray(items)) return []
	return items.map(normalizeCartLine).filter(Boolean)
}

/** @param {unknown[]} items */
export function cartItemsHavePrices(items) {
	if (!Array.isArray(items) || !items.length) return false
	return items.every((ci) => normalizeCartLine(ci) !== null)
}

/**
 * @param {import('vue').Ref<{ value?: { member?: { id?: string }, cart?: { cartItems?: unknown[] } } }>} user
 */
export function pickInitialCart(user) {
	if (isPostCheckoutRedirect()) return []

	const memberId = user?.value?.member?.id
	const dbItems = user?.value?.cart?.cartItems
	const legacyLocal = parseLocalCart('cart')
	const scopedLocal = memberId ? parseLocalCart(getCartStorageKey(memberId)) : null
	const localItems = scopedLocal ?? legacyLocal

	if (memberId && dbItems?.length) {
		const serverCart = normalizeCartItems(dbItems)
		if (serverCart.length && cartItemsHavePrices(serverCart)) {
			if (!localItems?.length || !cartItemsHavePrices(localItems)) return serverCart
			if (localItems.length !== serverCart.length) return serverCart
			const localIds = localItems
				.map((ci) => ci.item.id)
				.sort()
				.join(',')
			const serverIds = serverCart
				.map((ci) => ci.item.id)
				.sort()
				.join(',')
			if (localIds !== serverIds) return serverCart
			return localItems
		}
	}

	if (localItems?.length && cartItemsHavePrices(localItems)) return localItems
	if (dbItems?.length) {
		const serverCart = normalizeCartItems(dbItems)
		if (serverCart.length) return serverCart
	}
	return localItems?.length ? localItems : []
}

/** @param {unknown[]} cart @param {string | undefined} memberId */
export function persistLocalCart(cart, memberId) {
	window.localStorage.setItem(getCartStorageKey(memberId), JSON.stringify(cart))
}

/** @param {string | undefined} memberId */
export function clearLocalCart(memberId) {
	window.localStorage.removeItem(getCartStorageKey(memberId))
	window.localStorage.removeItem('cart')
}

/** After Stripe / $0 checkout redirect — local cart must not be re-saved to Mongo. */
export function isPostCheckoutRedirect() {
	if (typeof window === 'undefined') return false
	const params = new URLSearchParams(window.location.search)
	return params.get('stripe_checkout') === 'success'
}

/**
 * @param {import('vue').Ref} user
 * @param {import('vue').Ref<unknown[]>} cartRef
 */
export function hydrateCartFromServer(user, cartRef) {
	const dbItems = user?.value?.cart?.cartItems
	const memberId = user?.value?.member?.id
	if (!dbItems?.length) return false

	const normalized = normalizeCartItems(dbItems)
	if (!normalized.length) return false

	cartRef.value = normalized
	persistLocalCart(normalized, memberId)
	return true
}

/** @param {import('vue').Ref} user @param {import('vue').Ref<unknown[]>} cartRef */
export function migrateLegacyLocalCart(user, cartRef) {
	const memberId = user?.value?.member?.id
	if (!memberId) return

	const scoped = parseLocalCart(getCartStorageKey(memberId))
	if (scoped?.length) return

	const legacy = parseLocalCart('cart')
	if (!legacy?.length || !cartItemsHavePrices(legacy)) return

	if (!cartRef.value?.length || !cartItemsHavePrices(cartRef.value)) {
		cartRef.value = legacy
	}
	persistLocalCart(cartRef.value, memberId)
	window.localStorage.removeItem('cart')
}

/** @param {object} ci */
export function cartLineItemId(ci) {
	const item = ci?.item
	return typeof item === 'string' ? item : item?.id
}
